import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { portalApi } from "@/lib/portalApi";
import { PortalBrand } from "@/components/portal/PortalLayout";
import { toast } from "sonner";

/**
 * Validates a returnTo destination for portal post-login navigation.
 *
 * Accepts only internal relative /portal/... paths that are not themselves
 * authentication routes. Rejects anything external, protocol-relative, or
 * that would create an auth redirect loop.
 *
 * Returns the validated path, or "/portal" as the safe fallback.
 */
export function safePortalReturn(raw) {
  if (!raw || typeof raw !== "string") return "/portal";
  // Must be a relative path starting with /portal/
  if (!raw.startsWith("/portal/")) return "/portal";
  // Reject protocol-relative or anything with a scheme (catches "//evil.com", "http:", etc.)
  if (/^\/\/|:/.test(raw)) return "/portal";
  // Reject authentication routes — would create a redirect loop or expose the flow
  if (
    raw.startsWith("/portal/login") ||
    raw.startsWith("/portal/activate") ||
    raw.startsWith("/portal/reset-password")
  ) return "/portal";
  return raw;
}

function AuthShell({ children, title, subtitle }) {
  const [branding, setBranding] = useState({});
  useEffect(() => { portalApi.get("/portal/branding").then((r) => setBranding(r.data || {})).catch(() => {}); }, []);
  const sidebarColor = branding.branding_color || "#171B2E";
  return (
    <div className="min-h-screen flex bg-[#0F1222]" data-testid="portal-auth-shell">
      <div className="hidden lg:flex flex-col justify-between w-[44%] p-12 relative overflow-hidden" style={{ backgroundColor: sidebarColor }}>
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#F5A623]/10 blur-3xl" />
        <div className="absolute -left-16 bottom-0 h-72 w-72 rounded-full bg-[#F5A623]/5 blur-3xl" />
        <PortalBrand light logoUrl={branding.firm_logo_url} firmName={branding.firm_name} />
        <div className="relative z-10">
          <h2 className="font-heading text-3xl font-bold text-white leading-tight">Your taxes,<br /><span className="text-[#F5A623]">handled with care.</span></h2>
          <p className="text-slate-400 mt-4 text-sm leading-relaxed max-w-sm">Securely upload documents, track your filing, message your advisor, and chat with Penny — all in one place.</p>
          <div className="mt-8 space-y-3">
            {["Bank-grade secure document vault", "Real-time progress on your return", "Direct line to your tax advisor"].map((t) => (
              <div key={t} className="flex items-center gap-2.5 text-sm text-slate-300"><Icons.ShieldCheck className="h-4 w-4 text-[#F5A623]" />{t}</div>
            ))}
          </div>
        </div>
        <p className="relative z-10 text-xs text-slate-500">Chiang Mai &amp; Bangkok · taxteam@expattaxfirm.com</p>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex justify-center"><PortalBrand light logoUrl={branding.firm_logo_url} firmName={branding.firm_name} /></div>
          <h1 className="font-heading text-2xl font-bold text-white">{title}</h1>
          <p className="text-slate-400 text-sm mt-1 mb-6">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function PortalLogin() {
  const { login } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      // Return the client to their intended portal destination when one was preserved
      // by PortalProtectedRoute, otherwise fall back to the portal dashboard.
      navigate(safePortalReturn(location.state?.returnTo), { replace: true });
    }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sign-in failed"); }
    finally { setBusy(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post("/portal/auth/forgot-password", { email });
      setSent(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(false); }
  };

  if (mode === "forgot") {
    return (
      <AuthShell title="Reset your password" subtitle="We'll email you a secure link to set a new password.">
        {sent ? (
          <div className="text-center" data-testid="portal-forgot-sent">
            <Icons.MailCheck className="h-10 w-10 text-[#F5A623] mx-auto mb-3" />
            <p className="text-slate-300 text-sm">If an account exists for <span className="text-white font-medium">{email}</span>, a password reset link has been sent. The link is valid for 1 hour.</p>
            <button onClick={() => { setMode("login"); setSent(false); }} data-testid="portal-forgot-back-signin"
              className="inline-flex items-center gap-1.5 text-[#F5A623] text-sm mt-6 hover:underline">
              <Icons.ArrowLeft className="h-4 w-4" /> Back to sign in
            </button>
          </div>
        ) : (
          <>
            <form onSubmit={submitForgot} className="space-y-4" data-testid="portal-forgot-form">
              <Field icon="Mail" type="email" value={email} onChange={setEmail} placeholder="you@email.com" testid="portal-forgot-email" />
              <button type="submit" disabled={busy} data-testid="portal-forgot-submit"
                className="w-full bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-60 text-[#171B2E] font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2">
                {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Send className="h-4 w-4" />} Send reset link
              </button>
            </form>
            <button onClick={() => setMode("login")} data-testid="portal-forgot-cancel"
              className="inline-flex items-center gap-1.5 text-slate-500 text-sm mt-6 hover:text-slate-300">
              <Icons.ArrowLeft className="h-4 w-4" /> Back to sign in
            </button>
          </>
        )}
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your secure client portal.">
      <form onSubmit={submit} className="space-y-4" data-testid="portal-login-form">
        <Field icon="Mail" type="email" value={email} onChange={setEmail} placeholder="you@email.com" testid="portal-login-email" />
        <Field icon="Lock" type="password" value={password} onChange={setPassword} placeholder="Password" testid="portal-login-password" />
        <div className="flex justify-end">
          <button type="button" onClick={() => setMode("forgot")} data-testid="portal-forgot-password-link"
            className="text-xs text-[#F5A623] hover:underline">Forgot your password?</button>
        </div>
        <button type="submit" disabled={busy} data-testid="portal-login-submit"
          className="w-full bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-60 text-[#171B2E] font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2">
          {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.LogIn className="h-4 w-4" />} Sign In
        </button>
      </form>
      <p className="text-xs text-slate-500 mt-5 text-center">Need an invitation? Contact your tax advisor at <span className="text-[#F5A623]">taxteam@expattaxfirm.com</span></p>
      <p className="text-[11px] text-slate-600 mt-6 text-center"><Link to="/login" className="hover:text-slate-400">Staff login →</Link></p>
    </AuthShell>
  );
}

export function Field({ icon, type, value, onChange, placeholder, testid }) {
  const Ic = Icons[icon];
  return (
    <div className="relative">
      <Ic className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required data-testid={testid}
        className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/50 focus:border-[#F5A623]/50" />
    </div>
  );
}
